import { Contact, WeatherData, Alert, SafetyStatus } from "../types";

/**
 * Generates a human-friendly, conversational summary for the dashboard.
 * Focuses on a natural "Assistant" or "Jarvis-like" tone but warmer.
 */
export const generateHumanSummary = (
    userProfile: Contact | null,
    weather: WeatherData | null,
    hazards: Alert[],
    contacts: Contact[]
): string => {
    const now = new Date();
    const hour = now.getHours();

    // 1. Greeting based on time
    let greeting = "Hello";
    if (hour < 5) greeting = "Late night vigilance";
    else if (hour < 12) greeting = "Good morning";
    else if (hour < 17) greeting = "Good afternoon";
    else if (hour < 22) greeting = "Good evening";
    else greeting = "Good night";

    const name = userProfile?.name?.split(' ')[0] || "Guardian";
    let summary = `${greeting}, ${name}. `;

    // 2. Weather Context
    if (weather) {
        const temp = Math.round(weather.temp);
        const condition = weather.condition.toLowerCase();

        if (condition.includes('rain') || condition.includes('drizzle') || condition.includes('storm')) {
            summary += `It's a rainy ${temp}°C outside, so stay dry if you're heading out. `;
        } else if (temp > 30) {
            summary += `It's quite hot at ${temp}°C today. `;
        } else if (temp < 10) {
            summary += `It's a chilly ${temp}°C right now. `;
        } else {
            summary += `Conditions are ${condition} at ${temp}°C. `;
        }
    }

    // 3. Squad Status
    // If everyone is safe, say that. If someone is at risk, mention them.
    const unsafeContacts = contacts.filter(c => c.status !== SafetyStatus.SAFE);
    const safeCount = contacts.length - unsafeContacts.length;

    if (contacts.length === 0) {
        summary += "Your squad list is currently empty. ";
    } else if (unsafeContacts.length === 0) {
        summary += "All systems green—your entire squad is reported safe. ";
    } else if (unsafeContacts.length === 1) {
        const c = unsafeContacts[0];
        summary += `Attention: ${c.name.split(' ')[0]} is currently marked as ${c.status.toLowerCase()}. `;
    } else {
        summary += `Alert: ${unsafeContacts.length} squad members, including ${unsafeContacts[0].name.split(' ')[0]}, require attention. `;
    }

    // 4. Hazard Context
    // Only mention if something is actually close/relevant
    const severeHazards = hazards.filter(h => h.severity === 'critical' || h.severity === 'high');
    if (severeHazards.length > 0) {
        summary += `I've also detected ${severeHazards.length} critical alerts in your sector. Stay alert.`;
    } else {
        summary += "Local perimeter check shows no immediate threats.";
    }

    return summary;
};
